// Domain types for Automation system following DDD principles

export const TriggerType = {
  NEW_LEAD: 'new_lead',
  TAG_ADDED: 'tag_added',
  MESSAGE_RECEIVED: 'message_received',
  TIME_BASED: 'time_based'
} as const

export const ActionType = {
  SEND_MESSAGE: 'send_message',
  SEND_TEMPLATE: 'send_template',
  SAVE_TO_CRM: 'save_to_crm',
  ADD_TAG: 'add_tag'
} as const

export const ScheduleType = {
  IMMEDIATE: 'immediate',
  DELAYED: 'delayed',
  SCHEDULED: 'scheduled'
} as const

export const AutomationStatus = {
  ACTIVE: 'active',
  PAUSED: 'paused'
} as const

export type TriggerType = typeof TriggerType[keyof typeof TriggerType]
export type ActionType = typeof ActionType[keyof typeof ActionType]
export type ScheduleType = typeof ScheduleType[keyof typeof ScheduleType]
export type AutomationStatus = typeof AutomationStatus[keyof typeof AutomationStatus]

// Value Objects
export interface TriggerCondition {
  type: TriggerType
  source?: string
  condition?: string
  content?: string
}

export interface ActionConfig {
  type: ActionType
  content: string
  template?: string
  crm?: string
  tag?: string
}

export interface ScheduleConfig {
  type: ScheduleType
  delay?: number
  time?: string
}

// Aggregate Root
export interface Automation {
  id: string
  name: string
  description?: string
  trigger: TriggerCondition
  actions: ActionConfig[]
  schedule: ScheduleConfig
  status: AutomationStatus
  createdAt: Date
  updatedAt: Date
}

// DTOs for API communication
export interface CreateAutomationRequest {
  name: string
  description?: string
  trigger: TriggerCondition
  actions: ActionConfig[]
  schedule: ScheduleConfig
}

export interface UpdateAutomationRequest extends Partial<CreateAutomationRequest> {
  id: string
}

// Form data types
export interface AutomationFormData {
  name: string
  description: string
  triggerType: TriggerType
  triggerSource?: string
  triggerCondition?: string
  triggerContent?: string
  actions: ActionFormData[]
  scheduleType: ScheduleType
  scheduleDelay?: number
  scheduleTime?: string
}

export interface ActionFormData {
  type: ActionType
  content: string
  template?: string
  crm?: string
  tag?: string
  schedule: ScheduleConfig
}

// UI State types
export interface AutomationListState {
  automations: Automation[]
  loading: boolean
  error: string | null
}

export interface AutomationFormState {
  isOpen: boolean
  editingAutomation: Automation | null
  formData: AutomationFormData
  errors: Record<string, string>
}
